import {
    BuildingOffice2Icon,
    ChevronDownIcon,
    ChevronRightIcon,
    GlobeAltIcon,
    LockClosedIcon,
    MapPinIcon,
} from "@heroicons/react/24/outline";
import { EntityRegionModeOutput, RegionId, rootRegionParentId } from "@sp-crm/core";
import React from "react";
import { RegionTreeNode } from "./tree-utils";

interface TreeNodeProps {
    node: RegionTreeNode;
    visibilityMode: EntityRegionModeOutput | null;
    isExpanded: boolean;
    isEditable: boolean;
    tenantName: string;
    onToggleExpand: (regionId: RegionId) => void;
    onToggleVisibility: (regionId: RegionId) => void;
}

const statusConfig = {
    notShared: {
        label: "Not available",
        className: "bg-gray-100 text-gray-500",
    },
    visible: {
        label: "Available",
        className: "bg-yellow-100 text-yellow-700",
    },
    localOnly: {
        label: "This office only",
        className: "bg-yellow-100 text-yellow-700",
    },
    shareDown: {
        label: "Include child offices",
        className: "bg-green-100 text-green-700",
    },
    inherited: {
        label: "Via parent office",
        className: "bg-green-50 text-green-600 border border-green-200",
    },
};

function getStatusFromMode(mode: EntityRegionModeOutput | null, hasChildren: boolean) {
    if (mode === null) return statusConfig.notShared;
    if (mode === EntityRegionModeOutput.LocalOnly) {
        return hasChildren ? statusConfig.localOnly : statusConfig.visible;
    }
    if (mode === EntityRegionModeOutput.IncludeDescendants) return statusConfig.shareDown;
    if (mode === EntityRegionModeOutput.Inherited) return statusConfig.inherited;
    return statusConfig.notShared;
}

function getRegionIcon(node: RegionTreeNode) {
    if (node.depth === 0) {
        return <GlobeAltIcon className="h-5 w-5 text-gray-400" />;
    }
    if (node.children.length > 0) {
        return <BuildingOffice2Icon className="h-5 w-5 text-gray-400" />;
    }
    return <MapPinIcon className="h-5 w-5 text-gray-400" />;
}

export const TreeNode: React.FC<TreeNodeProps> = props => {
    const {
        node,
        visibilityMode,
        isExpanded,
        isEditable,
        tenantName,
        onToggleExpand,
        onToggleVisibility,
    } = props;

    const hasChildren = node.children.length > 0;
    const status = getStatusFromMode(visibilityMode, hasChildren);
    const isInherited = visibilityMode === EntityRegionModeOutput.Inherited;
    const canToggle = isEditable && !isInherited;

    const handleClick = () => {
        if (canToggle) {
            onToggleVisibility(node.region.id);
        }
    };

    const handleExpandClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggleExpand(node.region.id);
    };

    return (
        <div
            className={`flex items-center py-1.5 px-2 rounded ${
                canToggle ? "cursor-pointer hover:bg-gray-50" : ""
            } ${!isEditable ? "opacity-60" : ""}`}
            style={{ paddingLeft: `${node.depth * 24 + 8}px` }}
            onClick={handleClick}>
            <div className="flex items-center flex-1 min-w-0">
                <div className="w-5 h-5 flex items-center justify-center mr-1">
                    {hasChildren ? (
                        <button
                            type="button"
                            onClick={handleExpandClick}
                            className="p-0.5 hover:bg-gray-200 rounded">
                            {isExpanded ? (
                                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                            ) : (
                                <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                            )}
                        </button>
                    ) : null}
                </div>
                <div className="mr-2">{getRegionIcon(node)}</div>
                <span className="text-sm font-medium text-gray-700 truncate">
                    {node.region.parentRegionId === rootRegionParentId
                        ? tenantName
                        : node.region.name}
                </span>
                {!isEditable && (
                    <LockClosedIcon className="h-4 w-4 text-gray-400 ml-1 flex-shrink-0" />
                )}
            </div>
            <div
                className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${status.className}`}>
                {status.label}
            </div>
        </div>
    );
};
